// src/pages/Admin.js
import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
  getProducts, addProduct, updateProduct, deleteProduct,
  getEnquiries, getNiches, uploadProductImage,
} from "../firebase/services";
import { seedDatabase } from "../firebase/seed";
import { toast } from "../components/Toast";
import { BRAND, NICHES } from "../utils/constants";
import { formatDate, toSlug } from "../utils/helpers";
import "./Admin.css";

// ─── Login ────────────────────────────────────────────────────────────────────
function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast("Welcome back!", "success");
    } catch {
      setError("Invalid email or password. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      <div className="admin-login__box">
        <img src="/assets/logo.png" alt="Sunaharco" className="admin-login__logo"
          onError={(e) => { e.target.style.display = "none"; }} />
        <h2>Admin Panel</h2>
        <p>Sign in to manage Sunaharco content</p>
        <form onSubmit={handleSubmit}>
          <input type="email" placeholder="Email" value={email}
            onChange={(e) => setEmail(e.target.value)} required />
          <input type="password" placeholder="Password" value={password}
            onChange={(e) => setPassword(e.target.value)} required />
          {error && <p className="admin-login__error">{error}</p>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <p className="admin-login__note">
          🔐 Create an admin user in Firebase Console → Authentication → Users.
        </p>
      </div>
    </div>
  );
}

// ─── Image Uploader ───────────────────────────────────────────────────────────
function ImageUploader({ currentUrl, productSlug, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(currentUrl || "");
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // local preview
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const url = await uploadProductImage(file, productSlug || "new-product");
      onUploaded(url);
      toast("Image uploaded!", "success");
    } catch (err) {
      toast("Upload failed: " + err.message, "error");
      setPreview(currentUrl || "");
    }
    setUploading(false);
  };

  return (
    <div className="img-uploader">
      {preview ? (
        <div className="img-uploader__preview">
          <img src={preview} alt="Product" />
          <button type="button" className="img-uploader__clear"
            onClick={() => { setPreview(""); onUploaded(""); }}>✕</button>
        </div>
      ) : (
        <div className="img-uploader__placeholder" onClick={() => fileRef.current.click()}>
          <span>📷</span>
          <p>Click to upload product image</p>
          <small>PNG, JPG, WebP — max 5MB</small>
        </div>
      )}
      <div className="img-uploader__actions">
        <button type="button" className="btn btn-outline"
          onClick={() => fileRef.current.click()} disabled={uploading}
          style={{ fontSize: 13, padding: "8px 16px" }}>
          {uploading ? "Uploading…" : "Choose File"}
        </button>
        <input type="file" ref={fileRef} style={{ display: "none" }}
          accept="image/*" onChange={handleFile} />
        <span style={{ fontSize: 12, color: "var(--text-light)" }}>
          Or paste a URL below
        </span>
      </div>
    </div>
  );
}

// ─── Product Form ─────────────────────────────────────────────────────────────
const EMPTY_PRODUCT = {
  name: "", slug: "", tagline: "", description: "",
  category: NICHES.AGRICULTURE, niches: [],
  benefits: [], usageSuitability: [], howToUse: "",
  imageUrl: "", enquiryEmail: BRAND.email.sales,
  featured: false, active: true, comingSoon: false, order: 99,
  variants: [
    { size: "2 Kg", price: "", sku: "" },
    { size: "5 Kg", price: "", sku: "" },
  ],
  badges: [],
};

function ProductForm({ product, onSave, onCancel }) {
  const [form, setSaving]       = useState(product || EMPTY_PRODUCT);
  const [saving, setIsSaving]   = useState(false);
  const [nichesAvail, setNA]    = useState([]);

  // alias for clarity
  const form2 = form;
  const set = (k, v) => setSaving((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    getNiches().then(setNA).catch(() => setNA([]));
  }, []);

  const arrayField = (key, val) =>
    set(key, val.split("\n").map((s) => s.trim()));

  const toggleNiche = (id) =>
    set("niches", form2.niches.includes(id)
      ? form2.niches.filter((n) => n !== id)
      : [...form2.niches, id]);

  const updateVariant = (i, field, val) => {
    const next = [...form2.variants];
    next[i] = { ...next[i], [field]: val };
    set("variants", next);
  };

  const addVariant    = () => set("variants", [...form2.variants, { size: "", price: "", sku: "" }]);
  const removeVariant = (i) => set("variants", form2.variants.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try { await onSave(form2); }
    catch (err) { toast("Error: " + err.message, "error"); }
    setIsSaving(false);
  };

  return (
    <form className="product-form" onSubmit={handleSubmit}>
      <div className="pf-grid">

        {/* Name + Slug */}
        <div className="form-group">
          <label>Product Name *</label>
          <input value={form2.name} required
            onChange={(e) => {
              set("name", e.target.value);
              if (!product) set("slug", toSlug(e.target.value));
            }} />
        </div>
        <div className="form-group">
          <label>URL Slug *</label>
          <input value={form2.slug} required
            onChange={(e) => set("slug", e.target.value)} />
          <small>Used in URL: /products/<strong>{form2.slug || "slug"}</strong></small>
        </div>

        {/* Tagline + Category */}
        <div className="form-group">
          <label>Tagline</label>
          <input value={form2.tagline} onChange={(e) => set("tagline", e.target.value)}
            placeholder="Crafted for a Greener Tomorrow" />
        </div>
        <div className="form-group">
          <label>Category</label>
          <select value={form2.category} onChange={(e) => set("category", e.target.value)}>
            <option value="">Select Category</option>
            <option value={NICHES.AGRICULTURE}>Agriculture</option>
            <option value={NICHES.PREMIUM_MUGS}>Premium Mugs</option>
            <option value={NICHES.ENGINEERING_SERVICES}>Engineering Services</option>
          </select>
        </div>

        {/* Description */}
        <div className="form-group pf-full">
          <label>Description *</label>
          <textarea rows={4} value={form2.description} required
            onChange={(e) => set("description", e.target.value)} />
        </div>

        {/* Image */}
        <div className="form-group pf-full">
          <label>Product Image</label>
          <ImageUploader
            currentUrl={form2.imageUrl}
            productSlug={form2.slug}
            onUploaded={(url) => set("imageUrl", url)}
          />
          <div style={{ marginTop: 8 }}>
            <input value={form2.imageUrl}
              onChange={(e) => set("imageUrl", e.target.value)}
              placeholder="Or paste image URL: https://…" />
          </div>
        </div>

        {/* Variants */}
        <div className="form-group pf-full">
          <label>Pack Variants</label>
          <div className="pf-variants">
            {form2.variants.map((v, i) => (
              <div key={i} className="pf-variant-row">
                <input placeholder="Size (e.g. 5 Kg)" value={v.size}
                  onChange={(e) => updateVariant(i, "size", e.target.value)} />
                <input placeholder="Price (₹)" value={v.price}
                  onChange={(e) => updateVariant(i, "price", e.target.value)} />
                <input placeholder="SKU" value={v.sku}
                  onChange={(e) => updateVariant(i, "sku", e.target.value)} />
                <button type="button" className="pf-variant-remove"
                  onClick={() => removeVariant(i)}>✕</button>
              </div>
            ))}
            <button type="button" className="btn btn-outline"
              onClick={addVariant} style={{ fontSize: 13, padding: "8px 16px", width: "fit-content" }}>
              + Add Variant
            </button>
          </div>
        </div>

        {/* Benefits */}
        <div className="form-group">
          <label>Benefits <small>(one per line)</small></label>
          <textarea rows={5} value={form2.benefits.join("\n")}
            onChange={(e) => arrayField("benefits", e.target.value)}
            placeholder={"Improves soil aeration\nEnhances water retention\n..."} />
        </div>

        {/* Badges */}
        <div className="form-group">
          <label>Badges <small>(one per line)</small></label>
          <textarea rows={3} value={form2.badges.join("\n")}
            onChange={(e) => arrayField("badges", e.target.value)}
            placeholder={"100% Organic\nChemical-Free\n..."} />
        </div>

        {/* Usage Suitability */}
        <div className="form-group">
          <label>Suitable For <small>(one per line)</small></label>
          <textarea rows={3} value={form2.usageSuitability.join("\n")}
            onChange={(e) => arrayField("usageSuitability", e.target.value)}
            placeholder={"Agriculture\nHome gardening\nNurseries\n..."} />
        </div>

        {/* How to Use */}
        <div className="form-group">
          <label>How to Use</label>
          <textarea rows={3} value={form2.howToUse}
            onChange={(e) => set("howToUse", e.target.value)} />
        </div>

        {/* Enquiry Email */}
        <div className="form-group">
          <label>Enquiry Email</label>
          <input value={form2.enquiryEmail}
            onChange={(e) => set("enquiryEmail", e.target.value)} />
        </div>

        {/* Sort Order */}
        <div className="form-group">
          <label>Sort Order</label>
          <input type="number" value={form2.order}
            onChange={(e) => set("order", Number(e.target.value))}
            style={{ width: 80 }} />
        </div>

        {/* Niches */}
        <div className="form-group pf-full">
          <label>Niches (select all that apply)</label>
          <div className="pf-checkboxes">
            {nichesAvail.map((n) => (
              <label key={n.id} className="pf-checkbox">
                <input type="checkbox"
                  checked={form2.niches.includes(n.id)}
                  onChange={() => toggleNiche(n.id)} />
                {n.icon} {n.name}
              </label>
            ))}
          </div>
        </div>

        {/* Status Toggles */}
        <div className="form-group pf-full pf-toggles">
          {[
            ["featured",   "⭐ Featured on Homepage"],
            ["active",     "✅ Active (visible on site)"],
            ["comingSoon", "🔜 Coming Soon"],
          ].map(([key, label]) => (
            <label key={key} className="pf-toggle">
              <input type="checkbox" checked={!!form2[key]}
                onChange={(e) => set(key, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      <div className="pf-actions">
        <button type="button" className="btn btn-outline" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving…" : product ? "Update Product" : "Add Product"}
        </button>
      </div>
    </form>
  );
}

// ─── Main Admin Panel ─────────────────────────────────────────────────────────
export default function Admin() {
  const { user, logout, loading: authLoading } = useAuth();
  const [tab, setTab]             = useState("products");
  const [products, setProducts]   = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [dataLoading, setDL]      = useState(true);
  const [editProduct, setEdit]    = useState(null);   // product object | null
  const [showAdd, setShowAdd]     = useState(false);
  const [seeding, setSeeding]     = useState(false);
  const [searchQ, setSearchQ]     = useState("");

  const loadData = async () => {
    setDL(true);
    try {
      const [prods, enqs] = await Promise.all([getProducts(), getEnquiries()]);
      setProducts(prods);
      setEnquiries(enqs);
    } catch (err) {
      toast("Failed to load data: " + err.message, "error");
    }
    setDL(false);
  };

  useEffect(() => { if (user) loadData(); }, [user]);

  if (authLoading) return <div className="admin-loading">🌿 Loading…</div>;
  if (!user)       return <AdminLogin />;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdd = async (data) => {
    await addProduct(data);
    toast("Product added!", "success");
    setShowAdd(false);
    loadData();
  };

  const handleUpdate = async (data) => {
    await updateProduct(editProduct.id, data);
    toast("Product updated!", "success");
    setEdit(null);
    loadData();
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await deleteProduct(id);
    toast(`"${name}" deleted.`, "success");
    loadData();
  };

  const handleSeed = async () => {
    setSeeding(true);
    const result = await seedDatabase();
    setSeeding(false);
    if (result.success) {
      toast("Database seeded with initial data!", "success");
      loadData();
    } else {
      toast("Seed failed: " + result.error, "error");
    }
  };

  const closeForm = () => { setShowAdd(false); setEdit(null); };

  // Filtered products for search
  const visibleProducts = products.filter((p) =>
    !searchQ ||
    p.name?.toLowerCase().includes(searchQ.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQ.toLowerCase())
  );

  return (
    <div className="admin-panel">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand">
          <img src="/assets/logo.png" alt="Sunaharco"
            onError={(e) => { e.target.style.display = "none"; }} />
          <span>Admin CMS</span>
        </div>

        <nav className="admin-nav">
          {[
            ["products",  "📦 Products",   products.length],
            ["enquiries", "📬 Enquiries",  enquiries.filter(e => e.status === "new").length],
            ["settings",  "⚙️ Settings",   null],
          ].map(([key, label, count]) => (
            <button key={key}
              className={`admin-nav__item ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}>
              {label}
              {count > 0 && <span className="admin-nav__badge">{count}</span>}
            </button>
          ))}
        </nav>

        <div className="admin-sidebar__user">
          <span className="admin-sidebar__email">{user.email}</span>
          <button onClick={logout} className="btn btn-outline"
            style={{ fontSize: 12, padding: "6px 12px" }}>
            Sign Out
          </button>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────── */}
      <main className="admin-main">

        {/* ═══ PRODUCTS TAB ════════════════════════════════════════════════ */}
        {tab === "products" && (
          <div className="admin-section">
            <div className="admin-section__header">
              <h2>Products
                <span className="admin-section__count">{products.length}</span>
              </h2>
              <div className="admin-section__actions">
                <input className="admin-search" placeholder="Search products…"
                  value={searchQ} onChange={(e) => setSearchQ(e.target.value)} />
                <button className="btn btn-outline" onClick={handleSeed}
                  disabled={seeding} style={{ fontSize: 13 }}>
                  {seeding ? "Seeding…" : "🌱 Seed Data"}
                </button>
                <button className="btn btn-primary" onClick={() => { setShowAdd(true); setEdit(null); }}>
                  + Add Product
                </button>
              </div>
            </div>

            {/* Form panel */}
            {(showAdd || editProduct) && (
              <div className="admin-form-wrap">
                <div className="admin-form-wrap__header">
                  <h3>{editProduct ? `✏️ Edit: ${editProduct.name}` : "➕ New Product"}</h3>
                  <button className="admin-close-btn" onClick={closeForm}>✕</button>
                </div>
                <ProductForm
                  product={editProduct}
                  onSave={editProduct ? handleUpdate : handleAdd}
                  onCancel={closeForm}
                />
              </div>
            )}

            {dataLoading ? (
              <div className="admin-loading-inline">Loading products…</div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name / Slug</th>
                      <th>Category</th>
                      <th>Variants</th>
                      <th>Niches</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleProducts.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {p.imageUrl
                            ? <img src={p.imageUrl} alt={p.name} className="admin-thumb" />
                            : <div className="admin-thumb-placeholder">🌿</div>}
                        </td>
                        <td>
                          <strong>{p.name}</strong>
                          <br />
                          <span className="admin-slug">/{p.slug}</span>
                        </td>
                        <td>{p.category}</td>
                        <td>
                          {p.variants?.map((v) => (
                            <span key={v.sku} className="admin-variant-chip">{v.size}</span>
                          ))}
                        </td>
                        <td>
                          {p.niches?.map((n) => (
                            <span key={n} className="admin-niche-chip">{n}</span>
                          ))}
                        </td>
                        <td>
                          <span className={`admin-status ${p.active ? "admin-status--active" : "admin-status--inactive"}`}>
                            {p.active ? "Active" : "Inactive"}
                          </span>
                          {p.featured   && <span className="admin-status admin-status--featured">Featured</span>}
                          {p.comingSoon && <span className="admin-status admin-status--soon">Soon</span>}
                        </td>
                        <td>
                          <div className="admin-row-actions">
                            <button className="admin-btn-edit"
                              onClick={() => { setEdit(p); setShowAdd(false); window.scrollTo(0,0); }}>
                              Edit
                            </button>
                            <a href={`/products/${p.slug}`} target="_blank" rel="noreferrer"
                              className="admin-btn-view">View</a>
                            <button className="admin-btn-delete"
                              onClick={() => handleDelete(p.id, p.name)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {visibleProducts.length === 0 && (
                  <div className="admin-empty">
                    {searchQ ? `No products matching "${searchQ}".` : 'No products yet. Click "Seed Data" or "Add Product" to get started.'}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ ENQUIRIES TAB ═══════════════════════════════════════════════ */}
        {tab === "enquiries" && (
          <div className="admin-section">
            <div className="admin-section__header">
              <h2>Enquiries
                <span className="admin-section__count">{enquiries.length}</span>
              </h2>
              <button className="btn btn-outline" onClick={loadData} style={{ fontSize: 13 }}>
                ↻ Refresh
              </button>
            </div>

            {dataLoading ? (
              <div className="admin-loading-inline">Loading…</div>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Subject</th>
                      <th>Message</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((e) => (
                      <tr key={e.id}>
                        <td><strong>{e.name}</strong></td>
                        <td>
                          <a href={`mailto:${e.email}`} style={{ color: "var(--moss)" }}>
                            {e.email}
                          </a>
                        </td>
                        <td>{e.phone || "—"}</td>
                        <td>{e.subject || "—"}</td>
                        <td className="admin-msg-cell">
                          {e.message?.substring(0, 100)}{e.message?.length > 100 ? "…" : ""}
                        </td>
                        <td>{formatDate(e.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {enquiries.length === 0 && (
                  <div className="admin-empty">No enquiries yet. They'll appear here when visitors submit the contact form.</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ SETTINGS TAB ════════════════════════════════════════════════ */}
        {tab === "settings" && (
          <div className="admin-section">
            <div className="admin-section__header">
              <h2>Settings & Setup Guide</h2>
            </div>
            <div className="admin-settings">

              <div className="settings-card settings-card--ok">
                <h3>✅ Firebase Status</h3>
                <p>Logged in as: <strong>{user.email}</strong></p>
                <p>Firebase is connected and working.</p>
              </div>

              <div className="settings-card">
                <h3>📋 Firestore Security Rules</h3>
                <p>Copy and paste these rules in Firebase Console → Firestore → Rules:</p>
                <pre>{`rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /products/{doc} {
      allow read: if true;
      allow write: if request.auth != null;
    }
    match /niches/{doc} {
      allow read: if true;
      allow write: if request.auth != null;
    }
    match /enquiries/{doc} {
      allow read: if request.auth != null;
      allow create: if true;
      allow update, delete: if request.auth != null;
    }
    match /config/{doc} {
      allow read: if true;
      allow write: if request.auth != null;
    }
  }
}`}</pre>
              </div>

              <div className="settings-card">
                <h3>📱 WhatsApp Number</h3>
                <p>Update your WhatsApp number in <code>src/utils/constants.js</code>:</p>
                <pre>{`export const BRAND = {
  whatsappNumber: "918679085556", // country code + number
  ...
};`}</pre>
              </div>

              <div className="settings-card">
                <h3>🖼️ Product Images</h3>
                <p>Three ways to add images:</p>
                <ul>
                  <li><strong>Upload:</strong> Use the image upload button in the product form — uploads to Firebase Storage automatically</li>
                  <li><strong>URL:</strong> Paste any public image URL in the "Image URL" field</li>
                  <li><strong>Local:</strong> Place images in <code>public/assets/</code> and use paths like <code>/assets/product.png</code></li>
                </ul>
              </div>

              <div className="settings-card">
                <h3>🚀 Deploy to Firebase Hosting</h3>
                <pre>{`npm install -g firebase-tools
firebase login
# Edit .firebaserc → set your project ID
npm run build
firebase deploy`}</pre>
                <p>Your site will be live at: <code>https://your-project-id.web.app</code></p>
              </div>

              <div className="settings-card">
                <h3>🛒 eCommerce Upgrade Path</h3>
                <p>When ready to add payments:</p>
                <ul>
                  <li>Products already have <code>price</code> and <code>sku</code> in variant fields — just fill them</li>
                  <li>Add a <code>CartContext</code> for cart state management</li>
                  <li>Integrate <strong>Razorpay</strong> (recommended for India) or Stripe</li>
                  <li>Add an <code>orders</code> collection in Firestore</li>
                </ul>
              </div>

            </div>
          </div>
        )}
      </main>
    </div>
  );
}
